/**
 * Global data-change listener that invalidates React Query caches
 * when the backend broadcasts CRUD events for any entity.
 *
 * Mount this once at the app level (e.g., inside <App />) to ensure
 * all list and detail pages stay current without manual refreshes.
 *
 * Works alongside module-specific sockets (tasks, board, map) — those
 * continue to invalidate their own caches. This hook covers modules
 * that lack dedicated sockets (incidents, shelters, damage assessments, etc.).
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketFactory } from './useSocketFactory';
import { useTenantStore } from '@/store/tenant.store';

interface DataChangeEvent {
  entity: string;
  action: 'created' | 'updated' | 'deleted';
  id?: string;
  tenantId: string;
  incidentId?: string;
  timestamp: string;
}

/**
 * Maps backend entity names to React Query key prefixes that should
 * be invalidated when that entity changes.
 *
 * Prefix-based invalidation means `['incidents']` invalidates ALL
 * queries whose key starts with `[tenantId, 'incidents', ...]`.
 */
const ENTITY_QUERY_KEYS: Record<string, readonly string[][]> = {
  incidents: [['incidents'], ['incident']],
  shelters: [['shelters'], ['shelter'], ['shelter-summaries']],
  damage_assessments: [['damage-assessments'], ['damage-assessment']],
  contacts: [['contacts'], ['contact'], ['contact-groups']],
  supply_requests: [['supply-requests'], ['supply-request']],
  infrastructure: [['infrastructure'], ['road-closures'], ['utility-outages'], ['critical-infrastructure']],
  hazmat: [['hazmat']],
  credentials: [['credentials'], ['credential']],
  notifications: [['notifications']],
  lifelines: [['lifelines']],
  // `pods` and `business_continuity` are emitted by the backend (see the
  // DataChangeEntityType union in backend common/sockets/data-change.socket.ts)
  // but were MISSING here, so handleDataChange hit the `if (!keys) return`
  // below and dropped them on the floor. Neither module has a dedicated socket
  // or a refetchInterval, and useTenantQuery sets no refetch trigger on
  // staleness alone — so a mounted, focused POD board NEVER re-fetched. When a
  // logistics officer closed POD-3, every other operator's Active PODs tab kept
  // showing it as open indefinitely (until they window-blur/refocus, reconnect,
  // remount, or hit the manual refresh button) and kept routing residents and
  // supply trucks to a closed distribution point. Same for Business Continuity
  // sector / recovery-objective edits. Removing either line reopens that.
  // Adding a new backend entity? It must get a line here or it silently no-ops.
  pods: [['pods'], ['pod'], ['pod-items'], ['pod-distributions'], ['pod-stats']],
  business_continuity: [['business-continuity']],
  // W13-DEBRIS: debris estimates (list, detail and the incident roll-up).
  debris: [['debris']],
};

/** Entities whose changes should also invalidate the map feature cache */
const MAP_VISIBLE_ENTITIES = new Set([
  'incidents', 'shelters', 'damage_assessments', 'supply_requests',
  'infrastructure', 'hazmat', 'debris',
]);

export function useDataChangeSocket(enabled = true): { isConnected: boolean } {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  const handleDataChange = useCallback(
    (event: DataChangeEvent) => {
      const keys = ENTITY_QUERY_KEYS[event.entity];
      if (!keys) return;

      const tenantId = useTenantStore.getState().activeTenant?.id;
      if (!tenantId) return;

      for (const keyPrefix of keys) {
        // Invalidate BOTH cache-key shapes (same pattern as useBoardSocket.ts).
        // Queries created via useTenantQuery are cached under [tenantId, ...prefix],
        // but several DETAIL pages use a plain useQuery with an UN-prefixed key —
        // e.g. DamageAssessmentDetailPage (['damage-assessment', id]) and
        // ShelterDetailPage (['shelter', id]). Invalidating only the prefixed shape
        // left those pages showing pre-edit data for the full 5-minute default
        // staleTime after a remote edit: an operator could read a stale severity /
        // is_habitable value and make a re-entry call on life-safety data that had
        // already changed. Worst case of the extra bare invalidation is a redundant
        // refetch — the cache is already tenant-scoped and cleared on tenant switch.
        queryClient.invalidateQueries({ queryKey: [tenantId, ...keyPrefix] });
        queryClient.invalidateQueries({ queryKey: [...keyPrefix] });
      }

      // Also invalidate map cache for entities that appear on the map
      if (MAP_VISIBLE_ENTITIES.has(event.entity)) {
        queryClient.invalidateQueries({ queryKey: ['map-features'] });
        queryClient.invalidateQueries({ queryKey: ['map-counts'] });
      }
    },
    [queryClient],
  );

  const { isConnected } = useSocketFactory({
    namespace: '',
    displayName: '',
    enabled: enabled && !!activeTenant,
    showToasts: false,
    extraAuth: activeTenant ? { tenantId: activeTenant.id } : undefined,
    events: {
      'data:changed': handleDataChange,
    },
  });

  return { isConnected };
}
